import { useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { NV_PATHS, NvAccessDialog, NvErrorState, nvLoginPath } from "@/components/navigator";
import { useAuth } from "@/contexts/auth-context";
import { useRequest } from "@/hooks/use-request";
import { isMemberLevel } from "@/lib/access";
import { cn } from "@/lib/utils";
import { getMySummary, type MySection, type MySummary } from "@/services/navigator/my-page-service";

import { MY_PAGE_BASE, type MyPageContext } from "./my-page-shared";
import { WithdrawDialog } from "./my-profile";

// F07 · F09 My Page 틀 (Figma ver.6 "08 My Page", 546:1874 등): 위 프로필 띠(아바타 72 · 인사말 · 소속) + 탭 바(건수 알약) + 본문.
// 활동 요약 · 회원 구분은 설계서 v0.4에서 삭제됐어요. AI Assistant 상담 탭은 2차(1차에 없음).

const MENU: { key: MySection; label: string; path: string; count?: keyof MySummary["counts"] }[] = [
  { key: "profile", label: "내 정보", path: "" },
  { key: "bookmarks", label: "북마크", path: "bookmarks", count: "bookmarks" },
  { key: "compare", label: "비교함", path: "compare", count: "compare" },
  { key: "recent", label: "최근 조회 에이전트", path: "recent", count: "recent_views" },
  { key: "inquiries", label: "문의 이력", path: "inquiries", count: "inquiries" },
];

export function sectionOf(pathname: string): MySection {
  const rest = pathname.slice(MY_PAGE_BASE.length).replace(/^\//, "").split("/")[0];
  return (MENU.find((m) => m.path === rest)?.key ?? "profile") as MySection;
}

export function NavigatorMyPageLayout() {
  const { accessLevel, isLoading, logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [barSlot, setBarSlot] = useState<HTMLDivElement | null>(null);

  // Summary (profile + counts) is reloaded on every section change. The previous one stays on screen meanwhile,
  // and a failed refresh keeps it too (it used to blank the whole page); only a first load that fails shows [다시 시도].
  const signedIn = isMemberLevel(accessLevel);
  const { data: summary, error, reload: refresh } = useRequest((signal) => getMySummary(signal), [accessLevel, location.pathname], {
    enabled: !isLoading && signedIn,
    keepPreviousData: true,
  });

  if (isLoading) return null;
  if (accessLevel === "guest") return <Navigate to={nvLoginPath(location.pathname + location.search)} replace />;
  if (accessLevel === "pending") {
    return (
      <NvAccessDialog open reason="PENDING_APPROVAL" onOpenChange={(open) => !open && navigate(NV_PATHS.home, { replace: true })} />
    );
  }
  if (!summary) {
    if (!error) return <div className="flex-1" />;
    return (
      <div className="mx-auto w-full max-w-[1440px] px-5 pb-20 pt-10 xl:px-[120px]">
        <NvErrorState error={error} onRetry={refresh} />
      </div>
    );
  }

  const section = sectionOf(location.pathname);
  // 공급자는 1차에 내 정보 · 문의 이력만 (가정 A53)
  if (!summary.profile.sections.includes(section)) return <Navigate to={MY_PAGE_BASE} replace />;

  const p = summary.profile;
  const name = p.name ?? user?.requester_name ?? "";
  const affiliation = [p.company_name, [p.department, p.position].filter((v) => v && v !== "n/a").join(" ")].filter(Boolean).join(" · ");
  const menu = MENU.filter((m) => p.sections.includes(m.key));
  // 로그아웃 · 탈퇴 후 비회원 HOME (logout이 HOME으로 보내요)
  const doLogout = () => logout();

  return (
    <div className="flex flex-col">
      <section className="bg-nv-bg-page">
        <div className="mx-auto flex w-full max-w-[1440px] items-center gap-5 px-5 py-10 xl:px-[120px]">
          <span aria-hidden className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-nv-brand-soft text-[28px] leading-[34px] text-nv-text-primary">
            {name.slice(0, 1)}
          </span>
          <div className="flex min-w-0 flex-col gap-[6px]">
            <h1 className="truncate text-[26px] font-normal leading-[31px] text-nv-text-primary">{name}님, 안녕하세요</h1>
            {affiliation && <p className="truncate text-[14px] leading-[17px] text-nv-text-secondary">{affiliation}</p>}
          </div>
        </div>
      </section>

      <nav aria-label="My Page 메뉴" role="tablist" className="border-b border-nv-border">
        <div className="mx-auto flex w-full max-w-[1440px] items-end gap-2 px-5 xl:px-[120px]">
          {menu.map((m) => {
            const active = m.key === section;
            return (
              <Link
                key={m.key}
                role="tab"
                aria-selected={active}
                aria-current={active ? "page" : undefined}
                to={m.path ? `${MY_PAGE_BASE}/${m.path}` : MY_PAGE_BASE}
                // Figma ver.7 탭 바 높이 56: 현재 탭 55 (글자 y242, 아래 주황선 3) · 나머지 53 (글자 y244)
                className={cn(
                  "flex items-center gap-[6px] px-[14px] pt-[18px] text-[15px] font-medium leading-[18px]",
                  active ? "border-b-[3px] border-nv-brand pb-4 text-nv-text-primary" : "pb-[17px] text-nv-text-secondary hover:text-nv-text-primary",
                )}
              >
                {m.label}
                {m.count && (
                  <span
                    className={cn(
                      "inline-flex h-[15px] min-w-[21px] items-center justify-center rounded-nv-xs px-[7px] font-num text-[11px] font-bold leading-[13px]",
                      active ? "bg-nv-brand-subtle text-nv-text-primary" : "bg-nv-bg-page text-nv-text-secondary",
                    )}
                  >
                    {summary.counts[m.count]}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      <section className="mx-auto flex w-full max-w-[1440px] flex-col gap-5 px-5 pb-20 pt-9 xl:px-[120px]">
        <Outlet context={{ summary, refresh, onWithdraw: () => setWithdrawOpen(true), barSlot } satisfies MyPageContext} />
      </section>
      {/* 하단 바 자리: 본문 바로 아래에 두고 화면 아래에 붙어 따라와요 (끝까지 내리면 푸터 바로 위) */}
      <div ref={setBarSlot} className="sticky bottom-0 z-30" />

      <WithdrawDialog open={withdrawOpen} onOpenChange={setWithdrawOpen} onDone={doLogout} />
    </div>
  );
}
